import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useSession } from '@/store/session'
import { useTheme } from '@/hooks/useTheme'
import { cn } from '@/lib/cn'
import CommandPalette from '@/components/cabina/CommandPalette'

const IS_PREVIEW =
  import.meta.env.DEV &&
  typeof window !== 'undefined' &&
  new URLSearchParams(window.location.search).has('preview')

/**
 * CabinaTopbar — barra superior del lenguaje "Cabina de inteligencia".
 *
 * Reutilizable por todas las pantallas autenticadas (Home hoy; Búsqueda/Plan
 * después). Conserva la funcionalidad del AppLayout actual: navegación
 * (Inicio/Búsqueda/Plan), email de sesión y logout vía Supabase. Añade el
 * conmutador de tema claro/oscuro y un command-bar (⌘K) decorativo.
 */
const NAV = [
  { to: '/app', label: 'Inicio' },
  { to: '/app/busqueda', label: 'Búsqueda' },
  { to: '/app/plan', label: 'Plan' },
]

export default function CabinaTopbar() {
  const navigate = useNavigate()
  const location = useLocation()
  const session = useSession((s) => s.session)
  const clearSession = useSession((s) => s.clearSession)
  const { theme, toggleTheme } = useTheme()

  // Command palette (⌘K). Abre con el botón o con la tecla ⌘K / Ctrl+K.
  const [paletteOpen, setPaletteOpen] = useState(false)
  const [paletteQuery, setPaletteQuery] = useState('')
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault()
        setPaletteQuery('')
        setPaletteOpen((o) => !o)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Preview (bucle de fidelidad): `?cmdk` o `?cmdk=texto` abre el palette para
  // las capturas, opcionalmente pre-rellenado.
  useEffect(() => {
    if (!IS_PREVIEW) return
    const params = new URLSearchParams(window.location.search)
    if (params.has('cmdk')) {
      setPaletteQuery(params.get('cmdk') ?? '')
      setPaletteOpen(true)
    }
  }, [])

  async function handleLogout() {
    await supabase.auth.signOut()
    clearSession()
    navigate('/')
  }

  const email = session?.user.email ?? ''
  const initial = email ? email[0]!.toUpperCase() : 'M'

  return (
    <header className="topbar">
      <Link to="/app" className="brand">
        <span className="mark">M</span>MapeoData
      </Link>
      <nav className="nav">
        {NAV.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className={cn(location.pathname === item.to && 'active')}
          >
            {item.label}
          </Link>
        ))}
      </nav>
      <div className="topbar-right">
        <button
          type="button"
          className="cmdk"
          onClick={() => { setPaletteQuery(''); setPaletteOpen(true) }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="7" />
            <path d="M21 21l-4-4" />
          </svg>
          Buscar o ir a… <kbd>⌘K</kbd>
        </button>
        <button
          type="button"
          className="icon-btn"
          onClick={toggleTheme}
          title="Cambiar tema"
          aria-label="Cambiar tema"
        >
          {theme === 'dark' ? (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="4" />
              <path d="M12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.5 1.5M17.5 17.5L19 19M19 5l-1.5 1.5M6.5 17.5L5 19" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 12.8A9 9 0 1111.2 3a7 7 0 009.8 9.8z" />
            </svg>
          )}
        </button>
        {email && <span className="email">{email}</span>}
        <button type="button" className="linklike" onClick={handleLogout}>
          Salir
        </button>
        <span className="avatar">{initial}</span>
      </div>

      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
        initialQuery={paletteQuery}
      />
    </header>
  )
}
